/**
 * Fotos verkleinern, bevor sie in den Tresor wandern.
 *
 * Ein Telefonfoto einer Brevet-Karte misst zwölf Megapixel und wiegt vier
 * Megabyte. Lesbar ist die Karte lange davor — und die Ausweise wandern
 * verschlüsselt durch die Sicherungsdatei, wo jedes Byte doppelt zählt: In
 * JSON steht ein Byte als Zahl mit Komma, also viermal so breit wie im
 * Speicher. Vier Megabyte Foto werden so zu sechzehn Megabyte Datei.
 *
 * Verkleinert wird deshalb auf 2000 Pixel lange Kante und JPEG mit Güte 0.85:
 * Eine Karte im Format Scheckkarte hat damit rund 1200 Pixel je Seite und
 * bleibt bis zur letzten Ziffer lesbar, bei ungefähr 300 Kilobyte.
 *
 * PDF und alles andere bleibt, wie es ist — eine Versicherungspolice ist Text
 * und lässt sich nicht sinnvoll skalieren.
 */

export const MAX_KANTE = 2000;
export const GUETE = 0.85;

/**
 * Lohnt sich das Verkleinern?
 *
 * Reine Entscheidung, ohne Bild und ohne DOM — damit sie prüfbar ist. PNG
 * bleibt PNG: Ein Bildschirmfoto eines Ausweises hat oft Text auf Fläche, und
 * JPEG macht daraus Matsch. Verkleinert wird es trotzdem, nur eben verlustfrei
 * skaliert.
 */
export function brauchtVerkleinerung(typ, groesse, grenze = 400_000) {
  if (!String(typ ?? '').startsWith('image/')) return false;
  if (typ === 'image/svg+xml') return false;      // Vektor, nichts zu skalieren
  return groesse > grenze;
}

/** Zielformat: JPEG, ausser bei PNG mit Transparenz — dort bleibt PNG. */
export function zielTyp(typ) {
  return typ === 'image/png' ? 'image/png' : 'image/jpeg';
}

/** Neue Kantenlängen unter Beibehaltung des Seitenverhältnisses. */
export function neueGroesse(breite, hoehe, maxKante = MAX_KANTE) {
  const laengste = Math.max(breite, hoehe);
  if (laengste <= maxKante) return { breite, hoehe };
  const faktor = maxKante / laengste;
  return { breite: Math.round(breite * faktor), hoehe: Math.round(hoehe * faktor) };
}

/**
 * Eine Datei einlesen und, wenn es sich lohnt, verkleinern.
 *
 * Zurück kommt immer `{ name, typ, roh }` — auch wenn nichts geschehen ist.
 * Scheitert das Verkleinern (kaputtes Bild, fehlendes Canvas), kommt das
 * Original zurück: Lieber ein grosser Anhang als gar keiner.
 */
export async function alsAnhang(datei, { maxKante = MAX_KANTE, guete = GUETE } = {}) {
  const roh = await datei.arrayBuffer();
  if (!brauchtVerkleinerung(datei.type, roh.byteLength)) {
    return { name: datei.name, typ: datei.type || 'application/octet-stream', roh };
  }
  try {
    const kleiner = await verkleinere(datei, maxKante, guete);
    // Ein verkleinertes Bild, das groesser ist als das Original, waere ein
    // schlechter Tausch — das kommt bei schon optimierten JPEGs vor.
    return kleiner.byteLength < roh.byteLength
      ? { name: datei.name, typ: zielTyp(datei.type), roh: kleiner }
      : { name: datei.name, typ: datei.type, roh };
  } catch {
    return { name: datei.name, typ: datei.type, roh };
  }
}

async function verkleinere(datei, maxKante, guete) {
  const bild = await createImageBitmap(datei);
  const { breite, hoehe } = neueGroesse(bild.width, bild.height, maxKante);
  const flaeche = document.createElement('canvas');
  flaeche.width = breite;
  flaeche.height = hoehe;
  flaeche.getContext('2d').drawImage(bild, 0, 0, breite, hoehe);
  bild.close?.();
  const klecks = await new Promise((erfuellen) => {
    flaeche.toBlob(erfuellen, zielTyp(datei.type), guete);
  });
  if (!klecks) throw new Error('Kein Bild erzeugt.');
  return klecks.arrayBuffer();
}
